import { FC, useMemo, useRef, useCallback } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  Row,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DndContext, closestCenter, DragMoveEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from '@carbon/react';
import { Pagination } from 'components/pagination';
import { OpenPanelLeft, OpenPanelFilledLeft } from '@carbon/react/icons';
import { DebouncedInput } from 'components/debounced-input';
import { PlainLink } from 'components/link';
import { TableActions } from 'components/table/table-actions';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { formatNumber } from 'utils/number';
import { parseLocalDateTime } from 'utils/dates';
import {
  DragHeader,
  DragCell,
} from 'pages/packages/show/evaluation-result/draggable-table-components';
import {
  handleDragEnd,
  handleDragMove,
} from 'pages/packages/show/evaluation-result/utils';
import type { SearchAtom } from 'atoms/search-atom';
import type {
  CountyCoverageResponse,
  CountyCoverageFacets,
  CountyRow,
} from 'types/api-responses';
import type { CountyCoverageParams } from './atoms';
import styles from './county-coverage.module.scss';

interface CountyCoverageTableProps {
  searchResult: SearchAtom<
    CountyCoverageResponse,
    CountyCoverageParams,
    CountyCoverageFacets
  >;
  setSearchPayload: (
    fn: (current: CountyCoverageParams | undefined) => CountyCoverageParams
  ) => void;
  isFilterOpen: boolean;
  onToggleFilter: () => void;
  filterCount: number;
}

const columnHelper = createColumnHelper<CountyRow>();

export const CountyCoverageTable: FC<CountyCoverageTableProps> = ({
  searchResult,
  setSearchPayload,
  isFilterOpen,
  onToggleFilter,
  filterCount,
}) => {
  // Get dynamic column headers based on date filter type
  const dateFilterType =
    searchResult.currentPayload?.dateFilter?.type || 'file_date';

  const getDateTypeLabel = (type: string) => {
    switch (type) {
      case 'file_date':
        return 'File Date';
      case 'effective_date':
        return 'Effective Date';
      case 'instrument_date':
        return 'Instrument Date';
      case 'recorded_date':
        return 'Recorded Date';
      case 'created_at':
        return 'Created Date';
      default:
        return 'File Date';
    }
  };

  const dateLabel = getDateTypeLabel(dateFilterType);

  // Search handler
  const handleSearchChange = useCallback(
    (value: string | number) => {
      setSearchPayload((current) => ({
        ...current!,
        page: 1,
        text: value ? String(value).trim() : undefined,
      }));
    },
    [setSearchPayload]
  );

  // Column definitions
  const columns = useMemo(
    () => [
      columnHelper.accessor('countyShortName', {
        id: 'countyShortName',
        header: 'County',
        size: 180,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'text',
        },
      }),
      columnHelper.accessor('stateName', {
        id: 'stateName',
        header: 'State',
        size: 160,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'text',
        },
      }),
      columnHelper.accessor('coverageStartDate', {
        id: 'coverageStartDate',
        header: `Earliest ${dateLabel}`,
        size: 200,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value ? parseLocalDateTime(value) : '--';
        },
      }),
      columnHelper.accessor('coverageEndDate', {
        id: 'coverageEndDate',
        header: `Most Recent ${dateLabel}`,
        size: 220,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value ? parseLocalDateTime(value) : '--';
        },
      }),
      columnHelper.accessor('earliestDocument', {
        id: 'earliestDocument',
        header: `First Document (${dateLabel})`,
        size: 240,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const doc = info.getValue();
          const text = doc?.title;
          return doc?.href ? (
            <PlainLink
              href={doc.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </PlainLink>
          ) : (
            text
          );
        },
      }),
      columnHelper.accessor('mostRecentDocument', {
        id: 'mostRecentDocument',
        header: `Most Recent Document (${dateLabel})`,
        size: 280,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const doc = info.getValue();
          const text = doc?.title;
          return doc?.href ? (
            <PlainLink
              href={doc.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </PlainLink>
          ) : (
            text
          );
        },
      }),
      columnHelper.accessor('documentCount', {
        id: 'documentCount',
        header: 'Total Documents',
        size: 180,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value === 0
            ? '--'
            : formatNumber(value, 0, 0, 'decimal', true, '--');
        },
      }),
      columnHelper.accessor('documentsLast24Hours', {
        id: 'documentsLast24Hours',
        header: 'Last 24 Hours',
        size: 160,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value === 0
            ? '--'
            : formatNumber(value, 0, 0, 'decimal', true, '--');
        },
      }),
      columnHelper.accessor('documentsLast7Days', {
        id: 'documentsLast7Days',
        header: 'Last 7 Days',
        size: 160,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value === 0
            ? '--'
            : formatNumber(value, 0, 0, 'decimal', true, '--');
        },
      }),
      columnHelper.accessor('documentsLast30Days', {
        id: 'documentsLast30Days',
        header: 'Last Month',
        size: 160,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value === 0
            ? '--'
            : formatNumber(value, 0, 0, 'decimal', true, '--');
        },
      }),
      columnHelper.accessor('documentsLast6Months', {
        id: 'documentsLast6Months',
        header: 'Last 6 Months',
        size: 170,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value === 0
            ? '--'
            : formatNumber(value, 0, 0, 'decimal', true, '--');
        },
      }),
      columnHelper.accessor('percentageOfTotal', {
        id: 'percentageOfTotal',
        header: '% of Total',
        size: 150,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) =>
          `${formatNumber(info.getValue(), 2, 2, 'decimal', false, '--')}%`,
      }),
    ],
    [dateLabel]
  );

  const tableId = 'county-coverage-table';
  const columnIds = columns.map((col) => col.id as string);

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(tableId);

  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder: columnIds,
      initialColumnPinning: { left: [] },
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const sorting = useMemo(() => {
    const sortAttribute = searchResult.currentPayload?.sortAttribute;
    const sortDirection = searchResult.currentPayload?.sortDirection;

    if (!sortAttribute) return [];

    return [
      {
        id: sortAttribute,
        desc: sortDirection === 'DESC',
      },
    ];
  }, [
    searchResult.currentPayload?.sortAttribute,
    searchResult.currentPayload?.sortDirection,
  ]);

  const table = useReactTable({
    data:
      (searchResult.data as CountyCoverageResponse | undefined)?.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    columnResizeMode: 'onChange',
    state: {
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;

      if (newSorting.length === 0) {
        setSearchPayload((current) => ({
          ...current!,
          sortAttribute: undefined,
          sortDirection: undefined,
          page: 1,
        }));
      } else {
        const sort = newSorting[0];
        setSearchPayload((current) => ({
          ...current!,
          sortAttribute: sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
          page: 1,
        }));
      }
    },
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableColumnFilters: false,
  });

  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 20,
    scrollPaddingStart: 48,
    initialOffset: 0,
  });

  return (
    <div className={styles.tableContainer}>
      <div className={styles.stackTableArea} ref={parentRef}>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(e) =>
            handleDragEnd(
              e,
              columnPinning,
              columnOrder,
              setColumnOrder,
              setColumnPinning,
              hoveredColumnIdRef
            )
          }
          modifiers={[restrictToHorizontalAxis]}
          onDragMove={onDragMove}
        >
          <div className={styles.tableContext}>
            <Button
              kind="ghost"
              size="lg"
              onClick={onToggleFilter}
              aria-label={isFilterOpen ? 'Hide filters' : 'Show filters'}
              renderIcon={isFilterOpen ? OpenPanelFilledLeft : OpenPanelLeft}
            >
              {filterCount === 0
                ? isFilterOpen
                  ? 'Hide Filters'
                  : 'Show Filters'
                : filterCount === 1
                  ? '1 Filter Applied'
                  : `${filterCount} Filters Applied`}
            </Button>
            <div className={styles.stackSearchArea}>
              <DebouncedInput
                className={styles.stackSearchField}
                value={searchResult.currentPayload?.text || ''}
                onChange={handleSearchChange}
                placeholder="Search counties or states..."
              />
            </div>
            <TableActions
              data-testid="county-coverage-table-actions"
              table={table}
              isColumnFilterActive={false}
              setIsColumnFilterActive={() => {}}
              hideColumnFilterButton={true}
            />
          </div>

          <table className={styles.stackTable}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <DragHeader
                        key={header.id}
                        header={header}
                        hoveredColumnIdRef={hoveredColumnIdRef}
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody
              style={{
                minHeight: `${virtualizer.getTotalSize()}px`,
              }}
            >
              {searchResult.loading ? (
                <tr>
                  <td
                    colSpan={columnIds.length}
                    style={{ textAlign: 'center', padding: '2rem' }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnIds.length}
                    style={{ textAlign: 'center', padding: '2rem' }}
                  >
                    No results found
                  </td>
                </tr>
              ) : (
                virtualizer.getVirtualItems().map((virtualRow, index) => {
                  const row = rows[virtualRow.index] as Row<any>;
                  return (
                    <tr
                      key={row.id}
                      style={{
                        minHeight: `${virtualRow.size}px`,
                        transform: `translateY(${
                          virtualRow.start - index * virtualRow.size
                        }px)`,
                      }}
                    >
                      <SortableContext
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <DragCell
                            key={cell.id}
                            cell={cell}
                            row={row}
                            hoveredColumnIdRef={hoveredColumnIdRef}
                          />
                        ))}
                      </SortableContext>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </DndContext>
      </div>
      {/* Pagination */}
      {!searchResult.loading && (
        <Pagination
          page={
            (searchResult.data as CountyCoverageResponse | undefined)?.page || 1
          }
          pageSize={
            (searchResult.data as CountyCoverageResponse | undefined)
              ?.pageSize || 100
          }
          pageSizes={[25, 50, 100]}
          totalItems={
            (searchResult.data as CountyCoverageResponse | undefined)
              ?.totalCount || 0
          }
          onChange={({ page, pageSize }) => {
            setSearchPayload((current) => ({
              ...current!,
              page,
              pageSize,
            }));
          }}
        />
      )}
    </div>
  );
};
